class IS {
  /**
   * @link https://github.com/microsoft/TypeScript/issues/4196
   */
  static exist(v) {
    return v !== undefined && v !== null;
  }

  static nil(v) {
    return v === undefined || v === null;
  }

  /**
   * is string / array / object empty ?
   *
   * ! Note: null / undefined => false
   */
  static empty(v) {
    if (IS.string(v) || IS.array(v)) {
      return v.length === 0;
    }
    return !!v && Object.getOwnPropertyNames(v).length === 0 && Object.getOwnPropertySymbols(v).length === 0;
  }

  /**
   * - '12.34' = true
   * - 12.34 = true
   * - 1234 = true
   */
  static numeric(v) {
    return /^[+-]?\d+(\.\d+)?$/.test(`${v}`);
  }

  /**
   * - '1234' = true
   * - 1234 = true
   * - 12.34 = false
   */
  static int(v) {
    return /^[+-]?\d+$/.test(`${v}`);
  }

  /**
   * true / 'true' / '1' / 1 / 't' = true
   * - **Note**: case insensitive
   */
  static truely(v) {
    return v === true || /^t(rue)?$/i.test(`${v}`) || `${v}` === '1';
  }

  /**
   * false / 'false' / '0' / 0 / 'f' = true
   * - **Note**: case insensitive
   */
  static falsy(v) {
    return v === false || /^f(alse)?$/i.test(`${v}`) || `${v}` === '0';
  }

  static ipv4(v, privacy = false) {
    if (privacy) {
      return /^(10|127|169\.254|172\.(1[6-9]|2[0-9]|3[0-1])|192\.168)\./.test(`${v}`);
    }

    return /^(?:(?:\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.){3}(?:\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])$/.test(`${v}`);
  }

  static ipv6(v) {
    return /^((?=.*::)(?!.*::.+::)(::)?([\dA-F]{1,4}:(:|\b)|){5}|([\dA-F]{1,4}:){6})((([\dA-F]{1,4}((?!\3)::|:\b|$))|(?!\2\3)){2}|(((2[0-4]|1\d|[1-9])?\d|25[0-5])\.?\b){4})$/i.test(`${v}`);
  }

  static ip(v) {
    return IS.ipv4(v) || IS.ipv6(v);
  }

  static email(v) {
    // eslint-disable-next-line no-control-regex, no-useless-escape
    return /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i.test(`${v}`);
  }

  static url(v) {
    return /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/i.test(`${v}`);
  }

  static chinese(v) {
    return /^([\u4E00-\u9FCC\u3400-\u4DB5\uFA0E\uFA0F\uFA11\uFA13\uFA14\uFA1F\uFA21\uFA23\uFA24\uFA27-\uFA29]|[\ud840-\ud868][\udc00-\udfff]|\ud869[\udc00-\uded6\udf00-\udfff]|[\ud86a-\ud86c][\udc00-\udfff]|\ud86d[\udc00-\udf34\udf40-\udfff]|\ud86e[\udc00-\udc1d])+$/.test(`${v}`);
  }

  static iso8601(v) {
    try {
      return new Date(`${v}`).toISOString() === v;
    } catch (err) {
      return false;
    }
  }

  static string(v) {
    return typeof v === 'string' || v instanceof String;
  }

  static bool(v) {
    return v === true || v === false || v instanceof Boolean;
  }

  static array(v) {
    return Array.isArray ? Array.isArray(v) : Object.prototype.toString.call(v) === '[object Array]';
  }

  static date(v) {
    return !!v && Object.prototype.toString.call(v) === '[object Date]' && !Number.isNaN(v);
  }

  static func(v) {
    return typeof v === 'function';
  }

  static promise(v) {
    return !!v && Object.prototype.toString.call(v) === '[object Promise]';
  }

  static primitive(v) {
    return v === null || v === undefined || (typeof v !== 'object' && typeof v !== 'function');
  }
}

export default IS;
